"use client";

// Ужать картинку в браузере, прежде чем отправлять её на сервер.
//
// 🔴 Зачем это существует. Фото у нас лежат data-URL прямо в базе (хранилища
// файлов пока нет — вопрос Марату про S3/MinIO открыт). Снимок с телефона — это
// 3–8 МБ, в base64 ещё на треть больше. Без обработки один шаг технологии
// весил бы больше, чем вся таблица рецептов, и это ударило бы дважды: по
// размеру базы с бэкапами и по каждому открытию карты на кухонном телефоне.
//
// 1600 пикселей по большей стороне — предел, за которым разницы не видно ни на
// телефоне, ни в печатной карте (A4 при 150 dpi — это ~1240 точек по ширине).
// Качество 0.82 у JPEG — та точка, где артефакты ещё не читаются глазом.
//
// PNG превращается в JPEG намеренно: скриншот меню в PNG весит в разы больше
// фотографии того же размера. Исключение — прозрачность: логотип на прозрачном
// фоне после JPEG получил бы чёрный прямоугольник, поэтому такие остаются PNG.

const MAX_SIDE = 1600;
const JPEG_QUALITY = 0.82;

/** Файл слишком мал, чтобы его трогать: пересжатие только испортит картинку. */
const SKIP_UNDER_BYTES = 200_000;

export type CompressResult = {
  /** data-URL, готовый к отправке. */
  dataUrl: string;
  /** Сколько байт получилось (для сообщений человеку). */
  bytes: number;
};

/**
 * Прочитать файл и ужать, если он больше нужного.
 *
 * Возвращает исходник как есть, когда сжимать нечего или незачем: маленький
 * файл, не картинка, отсутствует canvas. Молчаливый отказ здесь правильный —
 * задача функции уменьшить вес, а не быть обязательным этапом.
 */
export async function compressImage(file: File): Promise<CompressResult> {
  const original = await readAsDataUrl(file);
  if (!file.type.startsWith("image/") || file.size < SKIP_UNDER_BYTES) {
    return { dataUrl: original, bytes: file.size };
  }

  try {
    const img = await loadImage(original);
    const scale = Math.min(1, MAX_SIDE / Math.max(img.width, img.height));
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return { dataUrl: original, bytes: file.size };

    // Прозрачность переживает только PNG. Белая подложка под JPEG была бы
    // сюрпризом: логотип с прозрачным фоном внезапно оказался бы в рамке.
    const keepPng = file.type === "image/png" && (await hasTransparency(file));
    if (!keepPng) {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, w, h);
    }
    ctx.drawImage(img, 0, 0, w, h);

    const out = keepPng
      ? canvas.toDataURL("image/png")
      : canvas.toDataURL("image/jpeg", JPEG_QUALITY);

    // Если «сжатие» сделало хуже (бывает на мелких PNG-скриншотах) — отдаём
    // исходник. Наша задача уменьшить файл, а не обязательно его переписать.
    return out.length < original.length
      ? { dataUrl: out, bytes: dataUrlBytes(out) }
      : { dataUrl: original, bytes: file.size };
  } catch {
    return { dataUrl: original, bytes: file.size };
  }
}

/** Размер картинки в байтах по её data-URL (base64 → 3/4 длины). */
export function dataUrlBytes(dataUrl: string): number {
  const i = dataUrl.indexOf(",");
  const b64 = i === -1 ? dataUrl : dataUrl.slice(i + 1);
  return Math.floor((b64.length * 3) / 4);
}

export function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result));
    fr.onerror = () => reject(new Error("Не удалось прочитать файл"));
    fr.readAsDataURL(file);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Не удалось открыть изображение"));
    img.src = src;
  });
}

/**
 * Есть ли в PNG хоть один неполностью непрозрачный пиксель.
 *
 * Смотрим по уменьшенной копии: полноразмерный проход по пикселям на снимке в
 * 12 мегапикселей заметно подвешивает вкладку, а нам нужен ответ «да/нет».
 */
async function hasTransparency(file: File): Promise<boolean> {
  try {
    const img = await loadImage(await readAsDataUrl(file));
    const side = 64;
    const canvas = document.createElement("canvas");
    canvas.width = side;
    canvas.height = side;
    const ctx = canvas.getContext("2d");
    if (!ctx) return true;
    ctx.drawImage(img, 0, 0, side, side);
    const { data } = ctx.getImageData(0, 0, side, side);
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 250) return true;
    }
    return false;
  } catch {
    // Не смогли выяснить — считаем, что прозрачность есть. Лишний PNG весит
    // больше, но не портит картинку; ошибочный JPEG портит безвозвратно.
    return true;
  }
}
